import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient, errorMessage, unwrap } from '../lib/api'
import type { Persona } from '../lib/postTypes'
import { queryKeys, useChannels, usePersonas } from '../lib/queries'
import { ErrorBox, InlineError, Notice, QueryState } from '../components/QueryState'
import { xConnectResult } from '../lib/xConnect'
import { platformLabel, supportsAutoDraft, usesPastedToken } from '../lib/platform'

/** チャンネルの接続状態 → 状態チップ（色だけに頼らず文字も出す） */
const CHANNEL_STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: '接続中', cls: 'gm-chip-ok' },
  auth_error: { label: '認証エラー', cls: 'gm-chip-bad' },
  disabled: { label: '停止中', cls: 'gm-chip-neutral' },
}

export function Personas() {
  const qc = useQueryClient()
  const personas = usePersonas()
  const [name, setName] = useState('')
  const [shopName, setShopName] = useState('')
  const [tone, setTone] = useState('')

  // X OAuth のコールバックは /personas?connected=x|x_failed&reason=... に戻ってくる（audit M3）。
  // 結果を表示したらクエリを消して、再読み込みで同じ表示が出ないようにする
  const [params, setParams] = useSearchParams()
  const [connectResult, setConnectResult] = useState<ReturnType<typeof xConnectResult>>(null)
  useEffect(() => {
    const connected = params.get('connected')
    if (!connected) return
    setConnectResult(xConnectResult(connected, params.get('reason')))
    if (connected === 'x') void qc.invalidateQueries({ queryKey: ['channels'] })
    setParams(p => { p.delete('connected'); p.delete('reason'); return p }, { replace: true })
  }, [params, setParams, qc])

  const create = useMutation({
    mutationFn: async () => unwrap(await apiClient.post('/personas', {
      name, profile: { shopName, tone },
    })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.personas }); setName(''); setShopName(''); setTone('') },
  })

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold leading-snug">ペルソナ</h1>

      {connectResult && (
        <Notice kind={connectResult.kind} onClose={() => setConnectResult(null)}>{connectResult.message}</Notice>
      )}

      <div className="gm-card p-4 md:p-5">
        <h2 className="mb-3 text-sm font-bold">新規作成</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          <input placeholder="名前（例: 本店の中の人）" aria-label="名前" value={name} onChange={e => setName(e.target.value)} className="gm-input" />
          <input placeholder="店舗名" aria-label="店舗名" value={shopName} onChange={e => setShopName(e.target.value)} className="gm-input" />
          <input placeholder="トーン（例: 親しみやすい）" aria-label="トーン" value={tone} onChange={e => setTone(e.target.value)} className="gm-input" />
        </div>
        <button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}
          className="gm-btn gm-btn-primary mt-3">作成</button>
        <InlineError message={create.isError ? `作成できませんでした: ${errorMessage(create.error)}` : null} className="mt-2" />
      </div>

      <QueryState query={personas} label="ペルソナ">
        {list => list.length === 0
          ? <p className="text-sm text-ink-muted">まだペルソナがありません。上のフォームから作成してください。</p>
          : <div className="space-y-3">{list.map(p => <PersonaCard key={p.id} persona={p} />)}</div>}
      </QueryState>
    </div>
  )
}

function PersonaCard({ persona }: { persona: Persona }) {
  const qc = useQueryClient()
  const channels = useChannels(persona.id)
  const [form, setForm] = useState<'wp' | 'xbyok' | 'threads' | 'instagram' | null>(null)
  /** トークンを入れ直す対象の Threads・Instagram チャンネル（認証切れからの復旧） */
  const [reconnectId, setReconnectId] = useState<string | null>(null)
  const [reconnectToken, setReconnectToken] = useState('')
  /** 入れ直しが済んだチャンネル（完了の案内を出す） */
  const [reconnectedId, setReconnectedId] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})

  const connect = useMutation({
    mutationFn: async () => {
      const path = form === 'wp' ? '/channels/wordpress'
        : form === 'threads' || form === 'instagram' ? `/channels/${form}/token`
          : '/channels/x/byok'
      unwrap(await apiClient.post(path, { personaId: persona.id, ...fields }))
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.channels(persona.id) }); setForm(null); setFields({}) },
  })

  // 既存の Threads・Instagram チャンネルのトークンを差し替える（チャンネルを作り直さないので、予約済みの投稿はそのまま出せる）
  const reconnect = useMutation({
    mutationFn: async (p: { id: string; platform: string }) =>
      unwrap(await apiClient.post(`/channels/${p.id}/${p.platform}/token`, { accessToken: reconnectToken })),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.channels(persona.id) })
      setReconnectId(null)
      setReconnectToken('')
      setReconnectedId(id)
      // フォームが閉じて入力欄が消えるので、開閉ボタンへフォーカスを戻す（キーボード操作で迷子にしない）
      document.getElementById(`reconnect-toggle-${id}`)?.focus()
    },
  })

  const POLICY_LABEL = { approve_only: '承認したものだけ投稿', auto_draft: '下書きとして自動投入', auto_publish: '完全自動投稿' }
  const setPolicy = useMutation({
    mutationFn: async (p: { id: string; publishPolicy: string }) =>
      unwrap(await apiClient.patch(`/channels/${p.id}`, { publishPolicy: p.publishPolicy })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.channels(persona.id) }),
  })

  return (
    <div className="gm-card p-4 md:p-5">
      <h2 className="text-sm font-bold">{persona.name} <span className="font-normal text-ink-muted">{persona.profile.shopName}</span></h2>
      <div className="mt-2 space-y-2">
        {channels.isError && (
          <ErrorBox message={`チャンネルの読み込みに失敗しました: ${errorMessage(channels.error)}`}
            onRetry={() => { void channels.refetch() }} />
        )}
        {channels.data?.map(c => (
          <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-bold">
              {platformLabel(c.platform)}{c.authMode === 'byok' && c.platform === 'x' ? ' (BYOK)' : ''}
            </span>
            <ChannelStatusChip status={c.status} />
            <span className="text-ink-muted">{c.handle}</span>
            <select value={c.publishPolicy} disabled={setPolicy.isPending}
              aria-label={`${c.handle} の投稿方針`}
              onChange={e => setPolicy.mutate({ id: c.id, publishPolicy: e.target.value })}
              className="gm-input ml-auto w-auto text-[13px]">
              {Object.entries(POLICY_LABEL)
                .filter(([k]) => supportsAutoDraft(c.platform) || k !== 'auto_draft')
                .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {usesPastedToken(c.platform) && c.status !== 'disabled' && (
              <button id={`reconnect-toggle-${c.id}`} type="button"
                onClick={() => { setReconnectId(reconnectId === c.id ? null : c.id); setReconnectToken(''); setReconnectedId(null); reconnect.reset() }}
                aria-expanded={reconnectId === c.id} className="gm-btn gm-btn-secondary text-[13px]">トークンを入れ直す</button>
            )}
            {reconnectedId === c.id && reconnectId !== c.id && (
              <p role="status" className="w-full text-xs text-ok">トークンを入れ直しました。</p>
            )}
            {reconnectId === c.id && (
              <form className="w-full space-y-2 rounded-ctl border border-line p-3"
                onSubmit={e => { e.preventDefault(); if (reconnectToken.trim() && !reconnect.isPending) reconnect.mutate({ id: c.id, platform: c.platform }) }}>
                <p className="text-xs text-ink-muted">同じ {platformLabel(c.platform)} アカウント（{c.handle}）の長期トークンを貼ってください。予約済みの投稿はそのまま出せます。</p>
                <input type="password" value={reconnectToken} onChange={e => setReconnectToken(e.target.value)}
                  placeholder="長期アクセストークン" aria-label={`${c.handle} の新しい長期アクセストークン`} className="gm-input" autoComplete="off" />
                <InlineError message={reconnect.isError ? errorMessage(reconnect.error) : null} />
                <button type="submit" disabled={reconnect.isPending || !reconnectToken.trim()}
                  className="gm-btn gm-btn-secondary">{reconnect.isPending ? '確認中…' : '入れ直す'}</button>
              </form>
            )}
          </div>
        ))}
        <InlineError message={setPolicy.isError ? `投稿方針を変更できませんでした: ${errorMessage(setPolicy.error)}` : null} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {/* WordPress はブログ機能ごと凍結（2026-10-09 決定）。記事を作れないので、新しい接続は受け付けない（接続済みの行はそのまま出す） */}
        <button type="button" disabled title="WordPress への記事の投稿は準備中です" className="gm-btn gm-btn-secondary">WordPress を接続（準備中）</button>
        <button onClick={() => { setForm(form === 'xbyok' ? null : 'xbyok'); setFields({}); connect.reset() }} aria-expanded={form === 'xbyok'} className="gm-btn gm-btn-secondary">X を接続（自分のキー）</button>
        {/* X かんたん接続（OAuth）は凍結（2026-10-09 決定）。サーバー側の /x/oauth/* は残してあるが、画面には出さない */}
        <button onClick={() => { setForm(form === 'threads' ? null : 'threads'); setFields({}); connect.reset() }} aria-expanded={form === 'threads'} className="gm-btn gm-btn-secondary">Threads を接続（トークン）</button>
        <button onClick={() => { setForm(form === 'instagram' ? null : 'instagram'); setFields({}); connect.reset() }} aria-expanded={form === 'instagram'} className="gm-btn gm-btn-secondary">Instagram を接続（トークン）</button>
      </div>
      {form === 'wp' && (
        <ConnectForm guide="wordpress" error={connect.isError ? errorMessage(connect.error) : ''} pending={connect.isPending} onSubmit={() => connect.mutate()}
          fields={[['siteUrl', 'サイトURL (https://...)'], ['username', 'ユーザー名'], ['appPassword', 'Application Password']]}
          values={fields} setValues={setFields} />
      )}
      {form === 'threads' && (
        <ConnectForm guide="threads" error={connect.isError ? errorMessage(connect.error) : ''} pending={connect.isPending} onSubmit={() => connect.mutate()}
          note="Meta for Developers で作ったアプリの「Threads API」で発行した長期アクセストークン（60日有効）を貼ります。権限は threads_basic と threads_content_publish が必要です。期限が近づくと自動で更新します。"
          fields={[['accessToken', '長期アクセストークン']]}
          values={fields} setValues={setFields} />
      )}
      {form === 'instagram' && (
        <ConnectForm guide="instagram" error={connect.isError ? errorMessage(connect.error) : ''} pending={connect.isPending} onSubmit={() => connect.mutate()}
          note="Instagram のプロアカウント（ビジネスかクリエイター）が必要です。Meta for Developers で作ったアプリの「Instagram ログインによる API 設定」で発行した長期アクセストークン（60日有効）を貼ります。権限は instagram_business_basic と instagram_business_content_publish が必要です。短期トークン（1時間）は貼らないでください — 1時間で切れて投稿が失敗します。Instagram への投稿には画像が必須です（設定の「画像生成」で「投稿に画像を付ける」をオンにし、コマ割りを使うなら形を「四角（2×2の4コマ）」か発動確率 0% にしてください）。"
          fields={[['accessToken', '長期アクセストークン']]}
          values={fields} setValues={setFields} />
      )}
      {form === 'xbyok' && (
        <ConnectForm guide="x" error={connect.isError ? errorMessage(connect.error) : ''} pending={connect.isPending} onSubmit={() => connect.mutate()}
          fields={[['apiKey', 'API Key'], ['apiSecret', 'API Secret'], ['accessToken', 'Access Token'], ['accessSecret', 'Access Token Secret']]}
          values={fields} setValues={setFields} />
      )}
    </div>
  )
}

function ConnectForm({ fields, values, setValues, onSubmit, pending, error, note, guide }: {
  /** フォームの上に出す説明（取り方など） */
  note?: string
  /** マニュアルの接続ガイド（/manual#<guide>）。キーやトークンの取り方を画面つきで説明している */
  guide: string
  fields: Array<[string, string]>
  values: Record<string, string>
  setValues: (v: Record<string, string>) => void
  onSubmit: () => void
  pending: boolean
  error: string
}) {
  return (
    <form className="mt-3 space-y-2 rounded-ctl border border-line p-3"
      onSubmit={e => { e.preventDefault(); if (!pending && !fields.some(([k]) => !values[k]?.trim())) onSubmit() }}>
      {note && <p className="text-xs text-ink-muted">{note}</p>}
      <p className="text-xs">
        <Link to={`/manual#${guide}`} className="font-bold text-primary hover:underline">接続のしかた（画面つきの手順）を見る</Link>
      </p>
      {fields.map(([key, label]) => (
        <input key={key} placeholder={label} aria-label={label} value={values[key] ?? ''} autoComplete="off"
          type={/secret|token|password/i.test(key) ? 'password' : 'text'}
          onChange={e => setValues({ ...values, [key]: e.target.value })}
          className="gm-input" />
      ))}
      <InlineError message={error} />
      <button type="submit" disabled={pending || fields.some(([k]) => !values[k]?.trim())}
        className="gm-btn gm-btn-secondary">
        {pending ? '接続テスト中…' : '接続する'}
      </button>
    </form>
  )
}

function ChannelStatusChip({ status }: { status: string }) {
  const st = CHANNEL_STATUS[status] ?? { label: status, cls: 'gm-chip-neutral' }
  return <span className={`gm-chip ${st.cls}`}>{st.label}</span>
}
